import { useTheme } from 'vuetify';
import { get as lo_get } from 'es-toolkit/compat';
import type { SectionStyle, ThemeType } from '~/types';
import type { WatchStopHandle } from 'vue';
import { useKookies } from 'x1-base/composables';
import { longMaxAge, LookupKeys } from '~/constants';

export const themeNameDefault = 'light';
export const CurrentThemeName = (name?: Nilish<string>) => useState('keySysCurrentThemeName', () => name || themeNameDefault);

export function useCustomThemes() {
    const $config = useRuntimeConfig();
    const useThemeObj = useTheme();
    const {
        themeCookiePfx
    } = LookupKeys;
    const {
        useReactiveKookie
    } = useKookies();
    const persistedThemeName = useReactiveKookie(themeCookiePfx, { maxAge: longMaxAge });
    const currentThemeName = CurrentThemeName(persistedThemeName.value);

    let unWatchConfigs: WatchStopHandle | undefined;

    const isCurrentThemeDark: ComputedRef<boolean> = computed<boolean>(() => useThemeObj.current?.value?.dark);

    const headerConfig = ref<SectionStyle['light'|'dark']>();
    const footerConfig = ref<SectionStyle['light'|'dark']>();

    function initLayout(): void {
        const t = persistedThemeName.value || themeNameDefault;

        // Set the theme
        changeTheme(t);

        // Start Watching for changes to colorMode; stop any previous watcher
        // first so repeated initLayout() calls don't orphan one on the
        // app-lifetime cookie ref
        unWatchConfigs?.();
        unWatchConfigs = watch(
            persistedThemeName,
            (newVal) => changeTheme(newVal)
        );
    }

    function changeTheme(themeName: Nilish<string>): void {
        themeName = themeName || themeNameDefault;
        useThemeObj.change(themeName);
        persistedThemeName.value = themeName;
        currentThemeName.value = themeName;
        const c = useThemeObj.name.value;
        headerConfig.value = lo_get($config, ['public', 'theme', 'header', c]);
        footerConfig.value = lo_get($config, ['public', 'theme', 'footer', c]);
    }

    onBeforeUnmount(() => {
        unWatchConfigs?.();
    });

    return {
        initLayout,
        currentThemeName,
        changeTheme,
        headerConfig,
        footerConfig,
        isCurrentThemeDark
    };
}
